import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { RouteAnalytics } from '../analytics/RouteAnalytics';
import { AuthProvider } from '../auth/AuthProvider';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { PageLoader } from '../components/ui/Loader';
import { paths } from '../config/paths';
import { useI18n } from '../i18n/I18nProvider';

const SignInRoute = lazy(() =>
  import('../pages/LoginPage').then((module) => ({
    default: module.LoginPage,
  }))
);
const SignOutRoute = lazy(() =>
  import('./routes/auth/sign-out').then((module) => ({
    default: module.SignOutRoute,
  }))
);
const TelegramEntryRoute = lazy(() =>
  import('./routes/auth/telegram-entry').then((module) => ({
    default: module.TelegramEntryRoute,
  }))
);
const AppRootRoute = lazy(() =>
  import('./routes/app/root').then((module) => ({
    default: module.AppRootRoute,
  }))
);
const NewProjectStartPage = lazy(() =>
  import('../pages/projects/newProjectStartPage/NewProjectStartPage').then((module) => ({
    default: module.NewProjectStartPage,
  }))
);
const ProjectNewChatPage = lazy(() =>
  import('../pages/projects/projectNewChatPage/ProjectNewChatPage').then((module) => ({
    default: module.ProjectNewChatPage,
  }))
);
const ProjectActiveChatPage = lazy(() =>
  import('../pages/projects/projectActiveChatPage/ProjectActiveChatPage').then((module) => ({
    default: module.ProjectActiveChatPage,
  }))
);
const MaterialPreviewPage = lazy(() =>
  import('../pages/projects/materialPreviewPage/MaterialPreviewPage').then((module) => ({
    default: module.MaterialPreviewPage,
  }))
);
const AppShell = lazy(() =>
  import('../components/layouts/AppShell').then((module) => ({
    default: module.AppShell,
  }))
);
const ApiTokensPage = lazy(() =>
  import('../pages/ApiTokensPage').then((module) => ({
    default: module.ApiTokensPage,
  }))
);
const OAuthConsentPage = lazy(() =>
  import('../pages/OAuthConsentPage').then((module) => ({
    default: module.OAuthConsentPage,
  }))
);
const PrivacyPolicyPage = lazy(() =>
  import('../pages/LegalDocumentPage').then((module) => ({
    default: module.PrivacyPolicyPage,
  }))
);
const TermsOfServicePage = lazy(() =>
  import('../pages/LegalDocumentPage').then((module) => ({
    default: module.TermsOfServicePage,
  }))
);
const SupportPage = lazy(() =>
  import('../pages/LegalDocumentPage').then((module) => ({
    default: module.SupportPage,
  }))
);
const WorkspaceLayout = lazy(() =>
  import('../pages/WorkspaceLayout').then((module) => ({
    default: module.WorkspaceLayout,
  }))
);
const AgentInstallationPage = lazy(() =>
  import('../pages/AgentInstallationPage').then((module) => ({
    default: module.AgentInstallationPage,
  }))
);
const ProjectLayout = lazy(() =>
  import('../pages/ProjectLayout').then((module) => ({
    default: module.ProjectLayout,
  }))
);
const ProjectOverviewPage = lazy(() =>
  import('../pages/projects/projectOverviewPage/ProjectOverviewPage').then((module) => ({
    default: module.ProjectOverviewPage,
  }))
);
const ProjectTabRedirect = lazy(() =>
  import('./routes/app/project-tab-redirect').then((module) => ({
    default: module.ProjectTabRedirect,
  }))
);
const ProjectInvitationPage = lazy(() =>
  import('../pages/ProjectInvitationPage').then((module) => ({
    default: module.ProjectInvitationPage,
  }))
);
const WorkspaceProjectsPage = lazy(() =>
  import('../pages/projects/workspaceProjectsPage/WorkspaceProjectsPage').then((module) => ({
    default: module.WorkspaceProjectsPage,
  }))
);
const SkillsPage = lazy(() =>
  import('../pages/SkillsPage').then((module) => ({
    default: module.SkillsPage,
  }))
);
const SkillDetailPage = lazy(() =>
  import('../pages/SkillDetailPage').then((module) => ({
    default: module.SkillDetailPage,
  }))
);
const ProjectSkillsRedirect = lazy(() =>
  import('../pages/SkillsPage').then((module) => ({
    default: module.ProjectSkillsRedirect,
  }))
);
const KnowledgePage = lazy(() =>
  import('../pages/KnowledgePage').then((module) => ({
    default: module.KnowledgePage,
  }))
);
const IntegrationsPage = lazy(() =>
  import('../pages/IntegrationsPage').then((module) => ({
    default: module.IntegrationsPage,
  }))
);
const PreflightPage = lazy(() =>
  import('../pages/PreflightPage').then((module) => ({
    default: module.PreflightPage,
  }))
);
const ReportsPage = lazy(() =>
  import('../pages/ReportsPage').then((module) => ({
    default: module.ReportsPage,
  }))
);
const NotificationsPage = lazy(() =>
  import('../pages/NotificationsPage').then((module) => ({
    default: module.NotificationsPage,
  }))
);
const UsagePage = lazy(() =>
  import('../pages/UsagePage').then((module) => ({
    default: module.UsagePage,
  }))
);
const UiKitPage = lazy(() =>
  import('../pages/UiKitPage').then((module) => ({
    default: module.UiKitPage,
  }))
);
const ChatWidgetsKitPage = lazy(() =>
  import('../pages/ChatWidgetsKitPage').then((module) => ({
    default: module.ChatWidgetsKitPage,
  }))
);
const LegacyTenantRedirect = lazy(() =>
  import('../pages/LegacyTenantRedirect').then((module) => ({
    default: module.LegacyTenantRedirect,
  }))
);

export function AppRouter() {
  const { t } = useI18n();
  return (
    <BrowserRouter>
      <RouteAnalytics />
      <AuthProvider>
        <Suspense fallback={<PageLoader label={t('loading')} />}>
          <Routes>
            <Route path={paths.telegram.path} element={<TelegramEntryRoute />} />
            <Route path={paths.auth.signIn.path} element={<SignInRoute />} />
            <Route path={paths.auth.loginAlias.path} element={<SignInRoute />} />
            <Route path={paths.auth.signOut.path} element={<SignOutRoute />} />
            <Route path="/oauth/consent" element={<OAuthConsentPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/ui" element={<UiKitPage />} />
            <Route path="/ui/chatWidgets" element={<ChatWidgetsKitPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/invite/:token" element={<ProjectInvitationPage />} />
              <Route element={<AppShell />}>
                <Route path={paths.app.root.path} element={<AppRootRoute />} />
                <Route path={paths.app.newProject.path} element={<NewProjectStartPage />} />
                <Route path="/app/account/api-tokens" element={<ApiTokensPage />} />
                <Route
                  path={paths.app.workspaceChat.path}
                  element={<Navigate to={paths.app.root.getHref()} replace />}
                />
                <Route element={<WorkspaceLayout />}>
                  <Route path={paths.app.projects.path} element={<WorkspaceProjectsPage />} />
                  <Route path="/app/agent-installation" element={<AgentInstallationPage />} />
                  <Route path="/app/skills" element={<SkillsPage />} />
                  <Route path="/app/skills/:skillId" element={<SkillDetailPage />} />
                  <Route path="/app/integrations" element={<IntegrationsPage />} />
                  <Route path="/app/notifications" element={<NotificationsPage />} />
                  <Route path="/app/usage" element={<UsagePage />} />
                </Route>
                <Route path="/app/projects/:projectId" element={<ProjectLayout />}>
                  <Route index element={<ProjectOverviewPage />} />
                  <Route path="chats" element={<ProjectTabRedirect tab="chats" />} />
                  <Route path="context" element={<ProjectTabRedirect tab="context" />} />
                  <Route path="import" element={<ProjectTabRedirect tab="materials" />} />
                  <Route path="runs" element={<ProjectTabRedirect tab="work-plan" />} />
                  <Route path="chat/new" element={<ProjectNewChatPage />} />
                  <Route path="chat/:chatId" element={<ProjectActiveChatPage />} />
                  <Route path="materials/previews/:previewId" element={<MaterialPreviewPage />} />
                  <Route path="skills" element={<ProjectSkillsRedirect />} />
                  <Route path="knowledge" element={<KnowledgePage />} />
                  <Route path="preflight" element={<PreflightPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                </Route>
                <Route path="/app/tenants/:tenantId/*" element={<LegacyTenantRedirect />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to={paths.app.root.getHref()} replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
